import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import { createGuestFromPerson, updateGuest } from '../services/guestService';
import type { GuestCreateFromPersonInput, GuestFormData, Guest } from '../types';

export function useCreateGuestFromPerson() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: GuestCreateFromPersonInput) => createGuestFromPerson(input),
    onSuccess: (createdGuest) => {
      queryClient.invalidateQueries({ queryKey: ['person', createdGuest.person.id] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      queryClient.invalidateQueries({ queryKey: ['person-guest', createdGuest.person.id] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      notify.success(t('guest.createFlow.success', 'Guest created successfully.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, { description: t('guest.createFlow.failed', 'Could not create the guest record.') });
    },
  });
}

export function useUpdateGuest() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      guestId,
      data,
      personId,
    }: {
      guestId: string;
      data: Partial<GuestFormData & { isActive?: boolean }>;
      personId?: string;
    }) => updateGuest(guestId, data, personId),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['guest', updated.id] });
      if (updated.person?.id) {
        queryClient.invalidateQueries({ queryKey: ['person', updated.person.id] });
        queryClient.invalidateQueries({ queryKey: ['person-guest', updated.person.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (error: Error) => {
      // Do not show automatic error toast — let the caller decide
      throw error;
    },
  });
}
